/**
 * API layer for Asertis Products & Categories.
 * All requests use authFetch for automatic Bearer token management.
 */
import { authFetch } from "./authService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_SESSION_URL = process.env.REACT_APP_API_SESSION_URL;

/**
 * Get session/temporal order data to extract sede_id, client info, etc.
 * @param {string} sessionId - Session ID from URL params
 */
const getSessionData = async (sessionId) => {
  try {
    const response = await authFetch(
      `${API_SESSION_URL}?session_id=${encodeURIComponent(sessionId)}`
    );
    if (!response.ok) {
      throw new Error(`Error fetching session: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error al obtener datos de sesión:", error);
    return null;
  }
};

/**
 * Get categories for a given sede.
 * @param {string} sedeId - Sede identifier
 * @returns {Array} Array of { category_id, name }
 */
const getCategories = async (sedeId) => {
  try {
    const response = await authFetch(
      `${API_BASE_URL}/products/categories?sede_id=${encodeURIComponent(sedeId)}`
    );
    if (!response.ok) {
      throw new Error(`Error fetching categories: ${response.status}`);
    }
    const data = await response.json();
    console.log("Categorías cargadas:", data.total || 0);
    return data.results || [];
  } catch (error) {
    console.error("Error al obtener categorías:", error);
    return [];
  }
};

/**
 * Get paginated products filtered by sede and optionally by category.
 * NOTE: The Asertis API accepts GET with body. Since browsers don't reliably
 * support body on GET requests, we send parameters as query params.
 * If the API requires body, change to POST.
 *
 * @param {string} sedeId - Sede identifier
 * @param {number} page - Page number (1-indexed)
 * @param {number} pageSize - Products per page
 * @param {string|null} categoryId - Optional category filter
 * @returns {{ products, total, totalPages, hasNext, hasPrev, page }}
 */
const getProducts = async (sedeId, page = 1, pageSize = 50, categoryId = null) => {
  try {
    let url = `${API_BASE_URL}/products?sede_id=${encodeURIComponent(sedeId)}&page=${page}&page_size=${pageSize}`;
    if (categoryId) {
      url += `&category_id=${encodeURIComponent(categoryId)}`;
    }

    const response = await authFetch(url);

    if (!response.ok) {
      throw new Error(`Error fetching products: ${response.status}`);
    }

    const data = await response.json();
    console.log(`Productos cargados: ${data.results?.length || 0} de ${data.total || 0}`);

    return {
      products: data.results || [],
      total: data.total || 0,
      totalPages: data.total_pages || 1,
      hasNext: data.has_next || false,
      hasPrev: data.has_prev || false,
      page: data.page || 1,
    };
  } catch (error) {
    console.error("Error al obtener productos:", error);
    return {
      products: [],
      total: 0,
      totalPages: 1,
      hasNext: false,
      hasPrev: false,
      page: 1,
    };
  }
};

export { getSessionData, getCategories, getProducts };
